<template>
    <div>
        <AppTitle>
            <template #title>รายการตรวจเช็ค</template>
            <template #subtitle> Wanon Niwat Hospital [5S SYSTEM WNH] </template>
            <template #breadcrumb>หน้าหลัก</template>
        </AppTitle>

        <div class="row">
            <div class="col-md-12">
                <div class="tile">
                    <Overlay :showOverlay="isLoading" />

                    <div class="tile-title-w-btn">
                        <h3 class="title">รายการตรวจเช็ค ทั้งหมด</h3>
                        <p>
                            <a class="btn btn-primary icon-btn" href="#" @click="openAddModal">
                                <i class="bi bi-plus-square me-2"></i> Add Item
                            </a>
                        </p>
                    </div>
                    <div class="tile-body">
                        <table class="table table-bordered table-hover">
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Name</th>
                                    <th scope="col">Manage</th>
                                </tr>
                            </thead>
                            <tbody v-if="items.length">
                                <tr v-for="(item, index) in items" :key="item.id">
                                    <th scope="row">{{ index + 1 }}</th>
                                    <td>{{ item.name }}</td>
                                    <td>
                                        <div class="btn-group btn-group-sm" role="group"
                                            aria-label="Basic mixed styles example">
                                            <button type="button" class="btn btn-warning" @click="openEditModal(item)">
                                                <i class="bi bi-pencil"></i> Edit
                                            </button>
                                            <button type="button" class="btn btn-danger" @click="handleDelete(item.id)">
                                                <i class="bi bi-trash"></i> Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="3" class="text-center">No data found</td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Modal -->
                        <div class="modal fade" id="myModal" tabindex="-1" aria-labelledby="exampleModalLabel"
                            aria-hidden="true">
                            <div class="modal-dialog modal-dialog-centered">
                                <div class="modal-content">
                                    <div class="modal-header">
                                        <h5 class="modal-title" id="modalLabel">
                                            {{ modalMode === 'edit' ? 'Edit Item' : 'Add Item' }}
                                        </h5>
                                        <button type="button" class="btn-close" aria-label="Close"
                                            @click="hideModal"></button>
                                    </div>
                                    <form @submit.prevent="handleSubmit(editingItem)">
                                        <div class="modal-body">
                                            <div class="mb-3">
                                                <label for="name" class="form-label">Name</label>
                                                <input type="text" id="name" class="form-control"
                                                    v-model="editingItem.name" required />
                                            </div>
                                        </div>
                                        <div class="modal-footer">
                                            <button type="button" class="btn btn-secondary"
                                                @click="hideModal">Close</button>
                                            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                                                <span v-if="isSubmitting">
                                                    <span class="spinner-border spinner-border-sm me-1" role="status"
                                                        aria-hidden="true"></span>
                                                    Saving...
                                                </span>
                                                <span v-else>Save changes</span>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { Modal } from 'bootstrap';
import { reactive, nextTick, ref, onMounted, onUnmounted } from 'vue';
import axiosInstance from '@/utils/axiosInstance';
import {
    showErrorAlert,
    showCreateAlert,
    showUpdateAlert,
    showDeleteAlert,
    showConfirm
} from '@/utils/sweetalert2-utils';
import Overlay from '@/components/Overlay.vue';
import AppTitle from '@/layouts/AppTitle.vue';

const items = ref([]);

const editingItem = reactive({
    name: '',
});

const isLoading = ref(true);

// Load data from API
const fetchData = async () => {
    isLoading.value = true;
    try {
        const response = await axiosInstance.get('/checklist');
        items.value = response.data;
    } catch (error) {
        showErrorAlert('ไม่สามารถโหลดข้อมูลได้');
    } finally {
        isLoading.value = false;
    }
};

// Delete data from API
const handleDelete = async (id) => {
    const result = await showConfirm();
    if (result.isConfirmed) {
        try {
            await axiosInstance.delete(`/checklist/${id}`);
            items.value = items.value.filter(item => item.id !== id);
            showDeleteAlert();
        } catch (error) {
            showErrorAlert('เกิดข้อผิดพลาดในการลบรายการ');
        }
    }
};

const isSubmitting = ref(false);

const handleSubmit = async (updatedItem) => {
    isSubmitting.value = true;
    try {
        if (modalMode.value === 'edit') {
            await axiosInstance.put(`/checklist/${updatedItem.id}`, updatedItem);
            showUpdateAlert();
        } else {
            await axiosInstance.post('/checklist', updatedItem);
            showCreateAlert();
        }
        fetchData();
        hideModal();
    } catch (error) {
        showErrorAlert('เกิดข้อผิดพลาดในการบันทึกรายการ');
    } finally {
        isSubmitting.value = false;
    }
};

const modalMode = ref('add');

const showModal = () => {
    nextTick(() => {
        const modalElement = document.getElementById('myModal');
        if (modalElement) {
            const modal = new Modal(modalElement);
            modal.show();
        }
    });
};

const hideModal = () => {
    const modalElement = document.getElementById('myModal');
    const modal = Modal.getInstance(modalElement);
    if (modal) modal.hide();

    Object.assign(editingItem, { id: null, name: '' }); // reset form
};

const openAddModal = () => {
    Object.assign(editingItem, { id: null, name: '' });
    modalMode.value = 'add';
    showModal();
};

const openEditModal = (item) => {
    Object.assign(editingItem, item);
    modalMode.value = 'edit';
    showModal();
};

onMounted(fetchData);

onUnmounted(() => {
    const modalElement = document.getElementById('myModal');
    const modal = Modal.getInstance(modalElement);
    if (modal) modal.dispose();
});
</script>
